import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Info } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct, fmtPeriod } from "./theme";
import { DeltaBadge, SourceChip, SkeletonCard, NoData, SectionTitle, SrTable } from "./components";
import PulseLayout from "./PulseLayout";
import { pulseGet } from "./api";

const ITALY_IMG = "https://images.unsplash.com/photo-1518098268026-4e89f1a2cd8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxpdGFseSUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODc4NDQ3MDB8MA&ixlib=rb-4.1.0&q=85";

const MoversTable = ({ title, rows, dm, t, lang, withLang, testid }) => {
  const T = tokens(dm);
  if (!rows || !rows.length) return null;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid={testid}>
      <h3 className="text-[11px] font-bold uppercase tracking-wider px-4 pt-4 pb-2" style={{ color: T.muted }}>{title}</h3>
      <table className="w-full text-sm">
        <thead className="sr-only">
          <tr><th>#</th><th>{t("world_movers_col_country")}</th><th>{t("world_movers_col_value")}</th><th>{t("yoy")}</th></tr>
        </thead>
        <tbody>
          {rows.map((m, i) => (
            <tr key={m.ref_area} className="pulse-row-hover cursor-pointer"
              style={{ borderTop: `1px solid ${T.border}` }}>
              <td className="px-4 py-2 w-8 text-xs" style={{ color: T.muted }}>{i + 1}</td>
              <td className="py-2">
                <Link to={withLang(`/pulse/country/${m.ref_area}`)} className="font-semibold hover:underline"
                  data-testid={`mover-link-${m.ref_area}`} style={{ color: tokens(dm).text }}>
                  {m.ref_area_label || m.ref_area}
                </Link>
              </td>
              <td className="py-2 text-right pulse-num">{fmtCompact(m.value, lang)}</td>
              <td className="px-4 py-2 text-right w-24"><DeltaBadge pct={m.yoy_pct} dm={dm} suffix=" " /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const FreshnessBadge = ({ c, freshest, dm, t, lang }) => {
  const T = tokens(dm);
  if (c.latest_month) {
    const fresh = c.latest_month === freshest;
    return (
      <span className="text-[10px] font-semibold px-1.5 py-0.5"
        data-testid={`country-freshness-${c.iso3}`}
        title={t("world_fresh_tip", { p: fmtPeriod(c.latest_month, lang) })}
        style={{
          background: fresh ? T.brandSoft : (dm ? "rgba(245,158,11,0.12)" : "#FEF3C7"),
          color: fresh ? T.accent : (dm ? "#FCD34D" : "#92400E"),
          borderRadius: 2,
        }}>
        {fmtPeriod(c.latest_month, lang)}
      </span>
    );
  }
  if (c.latest_annual) {
    return (
      <span className="text-[10px] px-1.5 py-0.5"
        data-testid={`country-freshness-${c.iso3}`}
        title={t("world_annual_tip", { p: c.latest_annual })}
        style={{ background: T.surface2, color: T.muted, borderRadius: 2 }}>
        {c.latest_annual}
      </span>
    );
  }
  return null;
};

export default function WorldPulse() {
  const [dm] = useDarkMode();
  const { t, lang, withLang } = usePulseLocale();
  const T = tokens(dm);
  const navigate = useNavigate();
  const [world, setWorld] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortMode, setSortMode] = useState("az");

  useEffect(() => {
    let on = true;
    Promise.allSettled([pulseGet("world"), pulseGet("countries")]).then(([w, c]) => {
      if (!on) return;
      if (w.status === "fulfilled") setWorld(w.value);
      if (c.status === "fulfilled") setCountries(c.value.countries || []);
      setLoading(false);
    });
    return () => { on = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return countries;
    return countries.filter((c) => c.label.toLowerCase().includes(needle) || c.iso3.toLowerCase().includes(needle));
  }, [countries, q]);

  // Freshness sort: monthly beats annual ("2026-06" > "2024-00"), ties by name.
  const visible = useMemo(() => {
    if (sortMode !== "fresh") return filtered;
    const key = (c) => c.latest_month || (c.latest_annual ? `${c.latest_annual}-00` : "");
    return [...filtered].sort((a, b) => key(b).localeCompare(key(a)) || a.label.localeCompare(b.label));
  }, [filtered, sortMode]);

  const freshestMonth = useMemo(
    () => countries.reduce((m, c) => (c.latest_month > m ? c.latest_month : m), ""),
    [countries]);

  const eu = world?.eu_nights_ytd;
  const td = world?.top_destination;
  const regions = world?.origin_regions;

  return (
    <PulseLayout active="world">
      <div className="pulse-fade-up">
        <h1 className="pulse-serif text-4xl sm:text-5xl font-medium" data-testid="world-pulse-title">{t("world_title")}</h1>
        <p className="text-sm mt-2 mb-8" style={{ color: T.muted }}>{t("world_sub")}</p>

        {/* Headline strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 mb-8" style={{ gap: 1, background: T.border, border: `1px solid ${T.border}` }}>
          {loading && [0, 1].map((i) => <SkeletonCard key={i} h={140} dm={dm} />)}
          {!loading && eu && (
            <div className="p-5" style={{ background: T.surface }} data-testid="world-kpi-eu-nights">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: T.muted }}>
                {t("world_eu_nights", { year: eu.year })} <span className="normal-case">({t("world_eu_nights_months", { month: fmtPeriod(eu.latest_month, lang) })})</span>
              </p>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="pulse-num text-4xl font-semibold">{fmtCompact(eu.value, lang)}</span>
                <DeltaBadge pct={eu.yoy_pct} dm={dm} />
              </div>
              <div className="mt-3"><SourceChip source="EUROSTAT" dataset={eu.dataset_code} period={eu.latest_month} dm={dm} /></div>
            </div>
          )}
          {!loading && td && (
            <div className="p-5" style={{ background: T.surface }} data-testid="world-kpi-top-destination">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: T.muted }}>{t("world_top_dest")}</p>
              <div className="flex items-baseline gap-3 flex-wrap">
                <Link to={withLang(`/pulse/country/${td.ref_area}`)} className="pulse-serif text-2xl font-semibold hover:underline" style={{ color: T.accent }}>
                  {td.ref_area_label}
                </Link>
                <span className="pulse-num text-4xl font-semibold">{fmtCompact(td.value, lang)}</span>
                <DeltaBadge pct={td.yoy_pct} dm={dm} />
              </div>
              <div className="mt-3"><SourceChip source={td.source_org} period={td.period} dm={dm} /></div>
            </div>
          )}
          {!loading && !eu && !td && <NoData dm={dm} h={140} />}
        </div>

        {/* Source regions — origin data, explicitly labeled (owner decision) */}
        {!loading && regions?.regions?.length > 0 && (
          <section className="mb-8" data-testid="world-origin-regions">
            <SectionTitle dm={dm} right={
              <span className="text-[11px] inline-flex items-center gap-1 cursor-help" style={{ color: T.muted }}
                title={t("world_regions_caveat", { total: fmtCompact(regions.reported_total, lang) })}>
                <Info className="w-3.5 h-3.5" aria-hidden="true" /> {t("world_regions_caveat", { total: fmtCompact(regions.reported_total, lang) }).split(":")[0]}
              </span>
            }>
              {t("world_regions_title", { period: regions.period })}
            </SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6" style={{ gap: 1, background: T.border, border: `1px solid ${T.border}` }}>
              {regions.regions.map((r) => (
                <div key={r.region} className="p-4" style={{ background: T.surface }} data-testid={`region-card-${r.region.replace(/\s+/g, "-")}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.muted }}>{r.label}</p>
                  <p className="pulse-num text-xl font-semibold">{fmtCompact(r.value, lang)}</p>
                  <DeltaBadge pct={r.yoy_pct} dm={dm} suffix=" " />
                </div>
              ))}
            </div>
            <p className="text-[11px] mt-2" style={{ color: T.muted }}>
              {t("world_regions_caveat", { total: fmtCompact(regions.reported_total, lang) })} — <span className="uppercase font-semibold">UN Tourism via OWID</span>
            </p>
            <SrTable caption={t("world_regions_title", { period: regions.period })}
              columns={[t("world_movers_col_country"), t("world_movers_col_value"), t("yoy")]}
              rows={regions.regions.map((r) => [r.label, fmtCompact(r.value, lang), fmtPct(r.yoy_pct, lang)])} />
          </section>
        )}

        {/* Top movers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {loading && [0, 1].map((i) => <SkeletonCard key={i} h={300} dm={dm} />)}
          {!loading && (
            <MoversTable dm={dm} t={t} lang={lang} withLang={withLang} testid="movers-arrivals"
              title={t("world_movers_arrivals", { period: world?.top_movers_arrivals?.[0]?.period || "" })}
              rows={world?.top_movers_arrivals} />
          )}
          {!loading && (
            <MoversTable dm={dm} t={t} lang={lang} withLang={withLang} testid="movers-nights"
              title={t("world_movers_nights")} rows={world?.top_movers_nights_ytd} />
          )}
        </div>

        {/* Country selector + Italy Deep Dive */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-4">
          <section className="lg:col-span-2" data-testid="country-selector">
            <div className="flex items-center justify-between">
              <SectionTitle dm={dm}>{t("world_country_search")}</SectionTitle>
              <span className="flex items-center gap-3 mb-3">
                <span className="inline-flex" role="group" aria-label={t("world_sort_label")}
                  style={{ border: `1px solid ${T.border}`, borderRadius: 2 }}>
                  {[["az", t("world_sort_az")], ["fresh", t("world_sort_fresh")]].map(([mode, label]) => (
                    <button key={mode} onClick={() => setSortMode(mode)}
                      data-testid={`country-sort-${mode}`} aria-pressed={sortMode === mode}
                      className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider"
                      style={sortMode === mode
                        ? { background: T.accent, color: dm ? "#0f1115" : "#ffffff" }
                        : { background: T.surface, color: T.muted }}>
                      {label}
                    </button>
                  ))}
                </span>
                <span className="text-[10px]" style={{ color: T.muted }}
                  data-testid="country-freshness-legend">{t("world_freshness_legend")}</span>
              </span>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: T.muted }} aria-hidden="true" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("world_country_search_ph")}
                aria-label={t("world_country_search")} data-testid="country-search-input"
                className="w-full pl-9 pr-3 py-2.5 text-sm outline-none"
                style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 2 }} />
            </div>
            <div className="max-h-72 overflow-y-auto" style={{ border: `1px solid ${T.border}` }}>
              {visible.slice(0, 400).map((c) => (
                <button key={c.iso3} onClick={() => navigate(withLang(`/pulse/country/${c.iso3}`))}
                  data-testid={`country-option-${c.iso3}`}
                  className="w-full flex items-center justify-between px-4 py-2 text-sm text-left pulse-row-hover"
                  style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, color: T.text }}>
                  <span className="font-medium">{c.label}</span>
                  <span className="flex items-center gap-2">
                    <FreshnessBadge c={c} freshest={freshestMonth} dm={dm} t={t} lang={lang} />
                    <span className="text-[10px]" style={{ color: T.muted }}>{c.iso3}</span>
                  </span>
                </button>
              ))}
              {!visible.length && <NoData dm={dm} h={80} />}
            </div>
          </section>

          <section aria-label={t("world_italy_title")}>
            <SectionTitle dm={dm}>{t("world_italy_title")}</SectionTitle>
            <Link to={withLang("/pulse/italy")} className="relative overflow-hidden block group"
              style={{ border: `1px solid ${T.border}` }} data-testid="italy-deep-dive-card">
              <img src={ITALY_IMG} alt="Tuscany, Italy — cypress-lined road" className="h-44 w-full object-cover" />
              <div className="absolute inset-0 flex flex-col justify-end p-4"
                style={{ background: "linear-gradient(180deg, rgba(15,17,21,0.05) 0%, rgba(15,17,21,0.85) 100%)" }}>
                <span className="self-start text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 mb-1"
                  style={{ background: "#34D399", color: "#064E3B", borderRadius: 2 }}>{t("world_italy_new")}</span>
                <span className="pulse-serif text-xl font-semibold text-white">{t("world_italy_title")}</span>
                <span className="text-xs text-gray-300 leading-snug mt-1">{t("world_italy_desc")}</span>
              </div>
            </Link>
          </section>
        </div>
      </div>
    </PulseLayout>
  );
}
